import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import '../LandingPage.css';

interface HeaderProps {
  /** When true, the header floats transparently over a dark hero. */
  overlay?: boolean;
}

function Header({ overlay = true }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = () => setIsMenuOpen(false);

  // Escape closes the mobile menu and hands focus back to the toggle.
  useEffect(() => {
    if (!isMenuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        burgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMenuOpen]);

  const handleHomeClick = (event: MouseEvent<HTMLAnchorElement>) => {
    closeMenu();
    // Already on the landing page: React Router won't re-navigate to "/",
    // so scroll back to the top ourselves.
    if (location.pathname === '/') {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleContactClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    closeMenu();
    if (location.pathname === '/') {
      document
        .getElementById('contact')
        ?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Coming from another route: navigate home with the hash so the
      // landing page can scroll to the contact section once it mounts.
      navigate('/#contact');
    }
  };

  const getNavLinkClass = ({ isActive }: { isActive: boolean }): string =>
    `nav-link ${isActive ? 'active' : ''}`;

  const solid = !overlay || scrolled;

  return (
    <header className={`header ${solid ? 'header--scrolled' : ''} ${isMenuOpen ? 'header--menu-open' : ''}`}>
      <div className="header-content">
        <NavLink to="/" className="logo" onClick={closeMenu}>
          EF<span>Soft</span>
        </NavLink>

        <button
          ref={burgerRef}
          className={`burger-menu ${isMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMenuOpen((v) => !v)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          aria-controls="site-nav"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div
          className={`nav-backdrop ${isMenuOpen ? 'open' : ''}`}
          onClick={closeMenu}
          aria-hidden="true"
        />

        <nav
          id="site-nav"
          className={`nav-menu ${isMenuOpen ? 'open' : ''}`}
          aria-label="Main"
        >
          <NavLink to="/" className={getNavLinkClass} onClick={handleHomeClick} end>
            Home
          </NavLink>
          <NavLink to="/products" className={getNavLinkClass} onClick={closeMenu}>
            Products
          </NavLink>
          <a href="/#contact" className="nav-cta" onClick={handleContactClick}>
            Get in touch
          </a>
        </nav>
      </div>
    </header>
  );
}

export default Header;
